import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { consultationService } from '../services/consultationApi';
import ProfileIncompleteWarning from '../components/ProfileIncompleteWarning';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import NoTokensBanner from '../components/NoTokensBanner';
import { renderMarkdown } from '../utils/renderMarkdown';

const MAX_CHARS = 1000;

const NumerologiaPage: React.FC = () => {
  const { user, refresh } = useAuth();
  const [question, setQuestion] = useState('');

  useEffect(() => { refresh().catch(() => {}); }, []);
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profile = (user as any)?.profile;
  const missingFields: string[] = [];
  if (!profile?.birth_date) missingFields.push('birth_date');

  // Numerologia de Expressão/Alma/Personalidade usa TODAS as letras do nome. Só o
  // primeiro nome → cálculo incompleto. Aviso não-bloqueante (o Número do Destino,
  // que vem da data de nascimento, continua saindo).
  const nameIncomplete =
    missingFields.length === 0 &&
    ((user as any)?.name || '').trim().split(/\s+/).filter(Boolean).length < 2;

  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    setAnswer('');
    try {
      const q = question.trim() || 'Faça uma análise completa da minha numerologia pitagórica.';
      const result = await consultationService.askAurora(q, 'numerologia');
      setAnswer(result.general_interpretation);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao consultar Aurora Lyz');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      <AuroraLoadingOverlay isLoading={isLoading} message="Calculando sua Numerologia..." />
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Numerologia Pitagórica</h1>
        <div className="w-20" />
      </div>

      <div className="container mx-auto px-4 py-5 sm:py-10 max-w-2xl">
        <div className="text-center mb-8">
          <div className="text-3xl sm:text-5xl mb-3">🔢</div>
          <h2 className="text-lg sm:text-2xl font-mystical font-bold mb-2">Numerologia Pitagórica</h2>
          <p className="text-white/55 text-sm leading-relaxed">
            Aurora Lyz calcula seus números pessoais — Destino, Expressão, Alma e Personalidade — e revela o que eles dizem sobre sua vida.
          </p>
          <p className="text-amber-400/80 text-xs mt-2">Consome 1 token por consulta</p>
        </div>

        {missingFields.length > 0 && (
          <ProfileIncompleteWarning missingFields={missingFields} profileSection="dados" />
        )}

        {nameIncomplete && (
          <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)' }}>
            <p className="text-amber-300 font-semibold text-sm mb-1">Complete seu nome</p>
            <p className="text-amber-200/80 text-xs mb-3">
              A numerologia calcula seus números de Expressão, Alma e Personalidade a partir de <strong>todas as letras do seu nome completo</strong>. Com apenas o primeiro nome, o resultado fica incompleto.
            </p>
            <Link
              to="/profile?tab=dados"
              className="inline-block px-4 py-2 rounded-xl text-xs font-semibold text-black transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}
            >
              Completar meu nome →
            </Link>
          </div>
        )}

        {missingFields.length === 0 && (
          <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
            <p className="text-white/50 text-xs mb-1">Dados para a leitura</p>
            <p className="text-white/85 text-sm">
              <span className="font-semibold">{(user as any)?.name}</span>
              {profile?.birth_date && <> · {new Date(profile.birth_date + 'T00:00:00').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</>}
            </p>
          </div>
        )}

        <AnimatePresence mode="wait">
          {!answer && (
            <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              <div className="relative mb-4">
                <textarea
                  value={question}
                  onChange={e => setQuestion(e.target.value.slice(0, MAX_CHARS))}
                  placeholder="Opcional: faça uma pergunta específica sobre sua numerologia. Ex: O que o meu número de destino revela sobre minha missão de vida?"
                  rows={4}
                  className="w-full border rounded-2xl px-5 py-4 focus:outline-none resize-none text-sm leading-relaxed"
                  style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                  disabled={isLoading || missingFields.length > 0}
                />
                <span className="absolute bottom-3 right-4 text-white/30 text-xs">{question.length}/{MAX_CHARS}</span>
              </div>

              <NoTokensBanner error={error} />

              <motion.button
                onClick={handleSubmit}
                disabled={isLoading || missingFields.length > 0}
                whileHover={!isLoading && missingFields.length === 0 ? { scale: 1.03 } : {}}
                whileTap={!isLoading && missingFields.length === 0 ? { scale: 0.97 } : {}}
                className="w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)' }}
              >
                {isLoading ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Aurora está calculando…</>
                ) : (
                  <><Sparkles size={18} />Calcular minha Numerologia</>
                )}
              </motion.button>
            </motion.div>
          )}

          {answer && (
            <motion.div key="answer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-4 sm:p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(139,92,246,0.10) 100%)', border: '1px solid rgba(139,92,246,0.3)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">🔢</span>
                  <span className="text-purple-300 font-mystical font-semibold text-sm">Aurora Lyz — Numerologia</span>
                </div>
                <div className="text-white/90 text-sm leading-relaxed text-justify">{renderMarkdown(answer)}</div>
              </div>
              <button onClick={() => { setAnswer(''); setQuestion(''); setError(null); }} className="w-full py-3 rounded-2xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium">
                Nova consulta
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NumerologiaPage;

